import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import frequencyMonths from "./data/frequencyMonths.json";
import timeSlotsData from "./data/timeSlotsData.json";
import { DEFAULT_FROM, DEFAULT_TO } from "../../../constants/dateTimeValues";
import { useJobCreate } from "../../../context/JobCreateContext";
import {
  validateFrequencyDetails,
  parseTime,
} from "../../../util_functions/helpers";

const FrequencyDatesTimesComponent = ({
  onUpdate,
  isFrequencyError,
  clearError,
}) => {
  const navigate = useNavigate();

  const { fetchJobSelectionsById, getJobCreateIdFromCookies } = useJobCreate();

  const defaultFrequencyDetails = {
    jobType: "",
    shiftTypes: [],
    supportType: "",

    payType: "",
    payRateHourly: null,
    payRateDaily: null,
    payRateAnnual: null,
    currency: "USD",

    payRanges: {
      hourly: { min: null, max: null },
      daily: { min: null, max: null },
      annual: { min: null, max: null },
    },

    sessionsPerWeek: 0,
    hoursPerSession: 0,
    supportStartDate: "",
    scheduling: "",
    additionalDetails: "",

    preferredDays: {
      Monday: false, Tuesday: false, Wednesday: false,
      Thursday: false, Friday: false, Saturday: false, Sunday: false,
    },
    timeSlots: {
      Monday: [], Tuesday: [], Wednesday: [],
      Thursday: [], Friday: [], Saturday: [], Sunday: [],
    },

    sessionRanges: [],
    totalHoursPerWeek: null,
  };




  const [frequencyDetails, setFrequencyDetails] = useState(
    defaultFrequencyDetails
  );

  const [dateSelectionType, setDateSelectionType] = useState("");

  const [selectedDay, setSelectedDay] = useState(1);
  const [selectedMonth, setSelectedMonth] = useState(1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [computedErrors, setComputedErrors] = useState([]);
  const [visibleErrors, setVisibleErrors] = useState([]);

  const hasErrorForField = (fieldName) =>
    visibleErrors.some((e) => e.field === fieldName);

  const sessionsPerWeekRef = useRef(null);
  const hoursPerSessionRef = useRef(null);
  const daySelectionRef = useRef(null);
  const errorMessageRef = useRef(null);
  const flexibleNotesRef = useRef(null);

  const hasAutoScrolledRef = useRef(false);
  const isAutoScrollingRef = useRef(false);

  const refs = {
    jobType: useRef(null),
    shiftTypes: useRef(null),
    pay: useRef(null),
    supportType: useRef(null),
    sessionsPerWeek: useRef(null),
    hoursPerSession: useRef(null),
    supportStartDate: useRef(null),
    scheduling: useRef(null),
    preferredDays: useRef(null),
  };

  const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];


  const JOB_TYPE_OPTIONS = ["Session-based", "Full-time", "Part-time", "Live-in"];

  const SHIFT_TYPE_OPTIONS = [
    { key: "day", label: "Day" },
    { key: "evening", label: "Evening" },
    { key: "night", label: "Night" },
  ];

  const showShiftTypePicker = [
    "Full-time",
    "Part-time",
    "Session-based"
  ].includes(frequencyDetails.jobType);

  const SalaryPanel = React.useMemo(() => SalaryRangeUSDPanel, []);

  const PAY_LIMITS = {
    hourly: { min: 0, max: 60, step: 1 },
    daily: { min: 0, max: 1500, step: 10 },
    annual: { min: 0, max: 250000, step: 1000 },
  };
  const clampTo = (n, { min, max }) => Math.max(min, Math.min(max, Number(n) || min));


  const isPreferred = (frequencyDetails.scheduling || "").toLowerCase() === "preferred";

  const getScrollRef = (field) => {
    if (refs[field]) return refs[field];
    if (field && field.startsWith("timeSlot-")) return refs.preferredDays;
    return null;
  };

  const formatDuration = (mins) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    const parts = [];
    if (h > 0) parts.push(`${h} hr${h === 1 ? "" : "s"}`);
    if (m > 0) parts.push(`${m} min${m === 1 ? "" : "s"}`);
    return parts.length ? parts.join(" ") : "0 mins";
  };

  const normalizeFrequency = (raw = {}) => {
    const base = { ...defaultFrequencyDetails, ...raw };

    const toNum = (v) => (v === "" || v == null ? null : (Number.isNaN(Number(v)) ? null : Number(v)));
    const cleanRange = (r) => ({ min: toNum(r?.min), max: toNum(r?.max) });

    const payRateHourly = toNum(base.payRateHourly);
    const payRateDaily = toNum(base.payRateDaily);
    const payRateAnnual = toNum(base.payRateAnnual);

    const payRanges = {
      hourly: cleanRange(base.payRanges?.hourly ?? {}),
      daily: cleanRange(base.payRanges?.daily ?? {}),
      annual: cleanRange(base.payRanges?.annual ?? {}),
    };

    const explicitType = ["hourly", "daily", "annual"].includes(base.payType) ? base.payType : "";
    let inferredType = explicitType;
    if (!inferredType) {
      const fixed = { hourly: payRateHourly, daily: payRateDaily, annual: payRateAnnual };
      const fixedUnits = Object.entries(fixed).filter(([, v]) => v != null);
      if (fixedUnits.length === 1) {
        inferredType = fixedUnits[0][0];
      } else {
        const rangeUnits = ["hourly", "daily", "annual"].filter(u => payRanges[u].min != null || payRanges[u].max != null);
        if (rangeUnits.length === 1) inferredType = rangeUnits[0];
      }
    }

    const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

    const preferredDays = DAYS.reduce((acc, d) => {
      acc[d] = !!(base.preferredDays && base.preferredDays[d]);
      return acc;
    }, {});

    const timeSlots = DAYS.reduce((acc, d) => {
      const arr = base.timeSlots?.[d];
      acc[d] = (Array.isArray(arr) ? arr : [])
        .filter(s => s && s.from && s.to)
        .map(s => ({ from: String(s.from), to: String(s.to) }));
      return acc;
    }, {});

    const sessionRanges = [];
    for (const day of DAYS) {
      if (preferredDays[day]) {
        for (const s of timeSlots[day] ?? []) {
          const fromMin = parseTime(s.from);
          const toMin = parseTime(s.to);
          if (Number.isFinite(fromMin) && Number.isFinite(toMin) && toMin > fromMin) {
            const minutes = toMin - fromMin;
            sessionRanges.push({
              day,
              from: s.from,
              to: s.to,
              minutes,
              label: formatDuration(minutes),
            });
          }
        }
      }
    }

    const sessionsPerWeek = (base.supportType === "One-Off")
      ? 1
      : toNum(base.sessionsPerWeek);

    const hoursPerSession = toNum(base.hoursPerSession);

    let totalHoursPerWeek = null;
    if ((base.supportType || "").toLowerCase() === "one-off") {
      totalHoursPerWeek = hoursPerSession;
    } else if ((base.scheduling || "").toLowerCase() === "preferred" && sessionRanges.length) {
      const totalMins = sessionRanges.reduce((sum, r) => sum + r.minutes, 0);
      totalHoursPerWeek = +(totalMins / 60).toFixed(2);
    } else if (sessionsPerWeek != null && hoursPerSession != null) {
      totalHoursPerWeek = +(sessionsPerWeek * hoursPerSession).toFixed(2);
    }

    return {
      jobType: base.jobType || "",
      shiftTypes: Array.isArray(base.shiftTypes) ? base.shiftTypes : [],

      supportType: base.supportType || "",

      payType: inferredType,
      payRateHourly,
      payRateDaily,
      payRateAnnual,
      payRanges,
      currency: base.currency || "USD",

      sessionsPerWeek,
      hoursPerSession,

      supportStartDate: base.supportStartDate || "",
      scheduling: base.scheduling || "",
      additionalDetails: base.additionalDetails || "",

      preferredDays,
      timeSlots,

      sessionRanges,
      totalHoursPerWeek,
    };
  };


  useEffect(() => {
    if (isFrequencyError) {
      const fallback = validateFrequencyDetails(frequencyDetails);
      setVisibleErrors(computedErrors.length ? computedErrors : fallback);
      smoothScrollToTop();
    } else {
      setVisibleErrors([]);
    }
  }, [isFrequencyError]);

  useEffect(() => {
    const jobCreateId = getJobCreateIdFromCookies();
    if (!jobCreateId) {
      navigate("/account/fs-dashboard/my-jobs/new/standard");
    } else {
      fetchJobSelectionsById(jobCreateId)
        .then((fetchedSelections) => {
          const normalized = normalizeFrequency(fetchedSelections?.frequencyDetails || {});
          setFrequencyDetails(normalized);

          const initialStartDate = normalized.supportStartDate;
          if (initialStartDate) {
            if (initialStartDate === "As Soon As Possible") {
              setDateSelectionType("As Soon As Possible");
            } else {
              setDateSelectionType("specificDate");
              const [month, day, year] = initialStartDate.split("/");
              setSelectedMonth(parseInt(month, 10) || 1);
              setSelectedDay(parseInt(day, 10) || 1);
              setSelectedYear(parseInt(year, 10) || new Date().getFullYear());
            }
          }
        })
        .catch(console.error);
    }
  }, [getJobCreateIdFromCookies, fetchJobSelectionsById, navigate]);

  useEffect(() => {
    if (frequencyDetails.supportType === "Ongoing") {
      sessionsPerWeekRef.current?.focus();
    } else if (frequencyDetails.supportType === "One-Off") {
      hoursPerSessionRef.current?.focus();
    }
  }, [frequencyDetails.supportType]);

  useEffect(() => {
    if (frequencyDetails.scheduling === "Flexible") {
      flexibleNotesRef.current?.focus();
    }
  }, [frequencyDetails.scheduling]);

  useEffect(() => {
    const changes = {};

    if (!frequencyDetails.payType) {
      changes.payType = "hourly";
      changes.payRanges = {
        ...frequencyDetails.payRanges,
        hourly: {
          min: PAY_LIMITS.hourly.min,
          max: PAY_LIMITS.hourly.max,
        },
      };
    }

    if (!selectedDay || selectedDay === 0) {
      const today = new Date();
      changes.supportStartDate = `${selectedMonth || 1}/${today.getDate()}/${selectedYear || today.getFullYear()}`;
      setSelectedDay(today.getDate());
    }

    if (!selectedYear || selectedYear === 0) {
      const thisYear = new Date().getFullYear();
      changes.supportStartDate = `${selectedMonth || 1}/${selectedDay || 1}/${thisYear}`;
      setSelectedYear(thisYear);
    }

    if (Object.keys(changes).length > 0) {
      notifyUpdate(changes);
    }
  }, [frequencyDetails, selectedDay, selectedYear, selectedMonth]);

  useEffect(() => {
    const isOngoing = (frequencyDetails.supportType || "").toLowerCase() === "ongoing";
    const isFlexible = (frequencyDetails.scheduling || "").toLowerCase() === "flexible";
    const n = frequencyDetails.sessionsPerWeek;
    const needsDefault = !(Number.isInteger(n)) || n < 1;

    if (isOngoing && isFlexible && needsDefault) {
      notifyUpdate({ sessionsPerWeek: 1 });
    }
  }, [
    frequencyDetails.supportType,
    frequencyDetails.scheduling,
    frequencyDetails.sessionsPerWeek
  ]);


  const smoothScrollToTop = () => {
    const container = daySelectionRef.current;
    if (container) {
      const scrollDuration = 600;
      const scrollStep = container.scrollTop / (scrollDuration / 15);

      const scrollInterval = setInterval(() => {
        if (container.scrollTop > 0) {
          container.scrollTop -= scrollStep;
        } else {
          clearInterval(scrollInterval);
        }
      }, 15);
    }
  };

  const clearErrorAndFields = (field) => {
    if (!isFrequencyError) return;
    const latest = validateFrequencyDetails(frequencyDetails);
    setVisibleErrors(latest);
  };

  const notifyUpdate = (changes) => {
    const merged = { ...frequencyDetails, ...changes };
    let updated = normalizeFrequency(merged);
    updated = sanitizePayForSelectedType(updated);
    const errors = validateFrequencyDetails(updated);

    setComputedErrors(errors);
    setFrequencyDetails(updated);

    const isValid = errors.length === 0;

    if (isValid) {
      console.log("✅ Frequency details to be sent:", updated);
    }

    onUpdate({ data: updated, errors, isValid });

    if (isFrequencyError) setVisibleErrors(errors);
    else setVisibleErrors([]);
  };

  const handleChangeJobType = (type) => {
    const changes = { jobType: type };

    if (type === "Live-in") {
      changes.shiftTypes = [];
    }
    notifyUpdate(changes);
    clearErrorAndFields("jobType");
    clearErrorAndFields("shiftTypes");
  };

  const toggleShiftType = (key) => {
    const current = frequencyDetails.shiftTypes ?? [];
    const next = current.includes(key)
      ? current.filter((k) => k !== key)
      : [...current, key];

    notifyUpdate({ shiftTypes: next });

    if (next.length > 0) clearErrorAndFields("shiftTypes");
  };

  const handleChangeSupportType = (type) => {
    const changes = { supportType: type };

    if (type === "One-Off") {
      changes.sessionsPerWeek = 1;
    }

    if (
      type === "Ongoing" &&
      (frequencyDetails.scheduling || "").toLowerCase() === "flexible" &&
      (!(Number.isInteger(frequencyDetails.sessionsPerWeek)) || frequencyDetails.sessionsPerWeek < 1)
    ) {
      changes.sessionsPerWeek = 1;
    }

    notifyUpdate(changes);
    clearErrorAndFields("supportType");
  };



  const handleChangeSessionsPerWeek = (e) => {
    const value = e.target.value.trim();
    const sessions = value === "" ? "" : parseInt(value);
    setFrequencyDetails((prev) => ({
      ...prev,
      sessionsPerWeek: isNaN(sessions) ? "" : sessions,
    }));
    notifyUpdate({
      sessionsPerWeek: isNaN(sessions) ? 0 : sessions,
    });
    clearError();
  };

  const handleChangeHoursPerSession = (e) => {
    const value = e.target.value;
    const hours = value === "" ? null : parseFloat(value);
    setFrequencyDetails((prev) => ({
      ...prev,
      hoursPerSession: hours,
    }));
    notifyUpdate({ hoursPerSession: hours ?? null });
    clearError();
  };


  const handleTotalHoursFocus = () => {
    if (frequencyDetails.hoursPerSession === 0) {
      setFrequencyDetails(prev => ({ ...prev, hoursPerSession: "" }));
    }
  };

  const handleSessionsFocus = () => {
    if (frequencyDetails.sessionsPerWeek === 0) {
      setFrequencyDetails(prev => ({
        ...prev,
        sessionsPerWeek: ""
      }));
    }
  };


  const handleChangeSupportStartDate = (selectedValue) => {
    let newStartDate =
      selectedValue === "specificDate"
        ? `${selectedMonth}/${selectedDay}/${selectedYear}`
        : selectedValue;

    notifyUpdate({ supportStartDate: newStartDate });
    setDateSelectionType(
      selectedValue === "specificDate" ? "specificDate" : selectedValue
    );
    clearError();
    clearErrorAndFields("supportStartDate");
  };

  const handleChangeScheduling = (schedule) => {
    const normalized = String(schedule).trim();
    const changes = { scheduling: normalized };

    if (
      normalized === "Flexible" &&
      (frequencyDetails.supportType || "").toLowerCase() === "ongoing" &&
      (!(Number.isInteger(frequencyDetails.sessionsPerWeek)) || frequencyDetails.sessionsPerWeek < 1)
    ) {
      changes.sessionsPerWeek = 1;
    }

    notifyUpdate(changes);
    clearError();
    clearErrorAndFields("scheduling");

    if (schedule === "Preferred") {
      hasAutoScrolledRef.current = false;
      requestAnimationFrame(smoothScrollToBottom);
    } else {
      hasAutoScrolledRef.current = false;
      isAutoScrollingRef.current = false;
    }
  };

  const handleChangeAdditionalDetails = (e) => {
    const text = e.target.value;
    notifyUpdate({ additionalDetails: text });
    clearError();
    clearErrorAndFields("additionalDetails");
  };

  const handleChangeSelectedDay = (e) => {
    const newDay = parseInt(e.target.value);
    setSelectedDay(newDay);
    notifyUpdate({
      supportStartDate: `${selectedMonth}/${newDay}/${selectedYear}`,
    });
    clearError();
  };

  const handleChangeSelectedMonth = (e) => {
    const newMonth = parseInt(e.target.value);
    setSelectedMonth(newMonth);
    notifyUpdate({
      supportStartDate: `${newMonth}/${selectedDay}/${selectedYear}`,
    });
    clearError();
  };

  const handleChangeSelectedYear = (e) => {
    const newYear = parseInt(e.target.value);
    setSelectedYear(newYear);
    notifyUpdate({
      supportStartDate: `${selectedMonth}/${selectedDay}/${newYear}`,
    });
    clearError();
  };

  const toggleDay = (day) => {
    const isOn = !!frequencyDetails.preferredDays?.[day];

    const newPreferredDays = {
      ...frequencyDetails.preferredDays,
      [day]: !isOn,
    };

    const newTimeSlots = { ...frequencyDetails.timeSlots };
    if (!newPreferredDays[day]) {
      newTimeSlots[day] = [];
    } else if ((newTimeSlots[day] ?? []).length === 0) {
      newTimeSlots[day] = [{ from: DEFAULT_FROM, to: DEFAULT_TO }];
    }

    notifyUpdate({ preferredDays: newPreferredDays, timeSlots: newTimeSlots });
    clearErrorAndFields("preferredDays");
  };


  const addTimeSlot = (day) => {
    const newSlots = [
      ...(frequencyDetails.timeSlots[day] || []),
      { from: DEFAULT_FROM, to: DEFAULT_TO },
    ];

    const updatedTimeSlots = {
      ...frequencyDetails.timeSlots,
      [day]: newSlots,
    };

    notifyUpdate({ timeSlots: updatedTimeSlots });
  };

  const removeTimeSlot = (day, index) => {
    const filteredSlots = frequencyDetails.timeSlots[day].filter(
      (_, idx) => idx !== index
    );

    const updatedTimeSlots = {
      ...frequencyDetails.timeSlots,
      [day]: filteredSlots,
    };

    notifyUpdate({ timeSlots: updatedTimeSlots });
  };

  const handleChangeTimeSlot = (day, index, field, value) => {
    const updatedSlots = frequencyDetails.timeSlots[day].map((slot, idx) => {
      if (idx === index) {
        return { ...slot, [field]: value };
      }
      return slot;
    });

    const updatedTimeSlots = {
      ...frequencyDetails.timeSlots,
      [day]: updatedSlots,
    };

    const currentSlot = updatedTimeSlots[day][index];
    const fromInMinutes = parseTime(currentSlot.from);
    const toInMinutes = parseTime(currentSlot.to);

    let errorMessage = "";
    if (fromInMinutes >= toInMinutes) {
      errorMessage = `Start time (${currentSlot.from}) cannot be later than end time (${currentSlot.to}).`;
    }

    updatedTimeSlots[day][index] = {
      ...currentSlot,
      error: errorMessage,
    };

    setFrequencyDetails((prev) => ({
      ...prev,
      timeSlots: updatedTimeSlots,
    }));
    notifyUpdate({ timeSlots: updatedTimeSlots });
  };

  const smoothScrollToBottom = () => {
    const el = daySelectionRef.current;
    if (!el || hasAutoScrolledRef.current || isAutoScrollingRef.current) return;

    hasAutoScrolledRef.current = true;
    isAutoScrollingRef.current = true;

    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });

    const done = () => { isAutoScrollingRef.current = false; el.removeEventListener("wheel", cancel, opts); el.removeEventListener("touchstart", cancel, opts); el.removeEventListener("keydown", cancel, opts); el.removeEventListener("scroll", onScroll, opts); };
    const cancel = () => { done(); };
    const onScroll = () => {
      const atBottom = Math.abs(el.scrollHeight - el.clientHeight - el.scrollTop) < 2;
      if (atBottom) done();
    };
    const opts = { passive: true };

    el.addEventListener("wheel", cancel, opts);
    el.addEventListener("touchstart", cancel, opts);
    el.addEventListener("keydown", cancel, opts);
    el.addEventListener("scroll", onScroll, opts);

    setTimeout(done, 800);
  };


  const placeCursorAtEnd = (event) => {
    const { target } = event;
    const length = target.value.length;
    event.preventDefault();
    target.setSelectionRange(length, length);
  };

  const hasValidSlot = (slot) => {
    const f = parseTime(slot?.from);
    const t = parseTime(slot?.to);
    return Number.isFinite(f) && Number.isFinite(t) && t > f;
  };

  const collectValidSlots = React.useMemo(() => {
    const out = [];
    for (const day of DAYS) {
      if (frequencyDetails.preferredDays?.[day]) {
        for (const s of frequencyDetails.timeSlots?.[day] ?? []) {
          if (hasValidSlot(s)) {
            out.push({
              day,
              from: s.from,
              to: s.to,
              minutes: parseTime(s.to) - parseTime(s.from),
            });
          }
        }
      }
    }
    return out;
  }, [frequencyDetails.preferredDays, frequencyDetails.timeSlots]);

  const sessionsPerWeekAuto = collectValidSlots.length;

  const durationsHuman = React.useMemo(
    () => collectValidSlots.map(s => formatDuration(s.minutes)),
    [collectValidSlots]
  );

  const hasAnyPreferredDay = React.useMemo(
    () => DAYS.some(d => !!frequencyDetails.preferredDays?.[d]),
    [DAYS, frequencyDetails.preferredDays]
  );

  const showSessionFields =
    (
      frequencyDetails.supportType === "One-Off" ||
      frequencyDetails.scheduling === "Flexible" ||
      frequencyDetails.scheduling === "Preferred" ||
      DAYS.some(d => frequencyDetails.preferredDays?.[d])
    );

  const sanitizePayForSelectedType = (fd) => {
    const out = { ...fd, payRanges: { ...(fd.payRanges || {}) } };
    const types = ["hourly", "daily", "annual"];
    const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
    const blank = { min: null, max: null };

    const selected = types.includes(out.payType) ? out.payType : "";

    types.forEach((t) => {
      if (t !== selected) {
        out[`payRate${cap(t)}`] = null;
        out.payRanges[t] = { ...blank };
      }
    });

    if (selected) {
      const fixed = out[`payRate${cap(selected)}`];
      const r = out.payRanges[selected] || blank;
      const hasRange = r.min != null || r.max != null;

      if (fixed != null) {
        out.payRanges[selected] = { ...blank };
      } else if (hasRange) {
        out[`payRate${cap(selected)}`] = null;
      }
    }

    return out;
  };


  useEffect(() => {
    if (!showSessionFields) return;

    const hasDerivedSlots = collectValidSlots.length > 0;
    const isManualHours =
      frequencyDetails.supportType === "One-Off" &&
      frequencyDetails.scheduling === "Flexible";

    if (isManualHours && !hasDerivedSlots) return;

    const totalMins = collectValidSlots.reduce((sum, s) => sum + s.minutes, 0);
    const avgHours = hasDerivedSlots ? totalMins / collectValidSlots.length / 60 : 0;
    const roundedHalf = Math.round(avgHours * 2) / 2;

    if (frequencyDetails.supportType === "One-Off") {
      if (hasDerivedSlots) {
        if (
          frequencyDetails.sessionsPerWeek !== 1 ||
          frequencyDetails.hoursPerSession !== roundedHalf
        ) {
          notifyUpdate({ sessionsPerWeek: 1, hoursPerSession: roundedHalf });
        }
      }
      return;
    }

    if (hasDerivedSlots) {
      if (
        frequencyDetails.sessionsPerWeek !== collectValidSlots.length ||
        frequencyDetails.hoursPerSession !== roundedHalf
      ) {
        notifyUpdate({
          sessionsPerWeek: collectValidSlots.length,
          hoursPerSession: roundedHalf,
        });
      }
    }
  }, [
    showSessionFields,
    collectValidSlots,
    frequencyDetails.supportType,
    frequencyDetails.scheduling,
    frequencyDetails.sessionsPerWeek,
    frequencyDetails.hoursPerSession,
  ]);

  function SalaryRangeUSDPanel({
    type,
    value,
    range,
    onTypeChange,
    onCommit,
    limits,
  }) {
    const LIMITS = React.useMemo(
      () =>
        limits || {
          hourly: { min: 0, max: 60, step: 1 },
          daily: { min: 0, max: 1500, step: 10 },
          annual: { min: 0, max: 250000, step: 1000 },
        },
      [limits]
    );

    const activeType = type || "hourly";

    const clampTo = (n, { min, max }) => Math.max(min, Math.min(max, Number(n)));

    const fmtUSD = React.useMemo(
      () => new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }),
      []
    );

    const sliderRef = React.useRef(null);
    const minRef = React.useRef(null);
    const maxRef = React.useRef(null);

    const commitRef = React.useRef(onCommit);
    React.useEffect(() => { commitRef.current = onCommit; }, [onCommit]);

    React.useEffect(() => {
      const el = sliderRef.current;
      if (!el || !window.noUiSlider) return;
      const cfg = LIMITS[activeType];

      if (el.noUiSlider) el.noUiSlider.destroy();

      const startLo =
        range && range.min != null ? clampTo(range.min, cfg) : cfg.min;
      const startHi =
        range && range.max != null
          ? clampTo(range.max, cfg)
          : value != null
            ? clampTo(value, cfg)
            : cfg.max;

      window.noUiSlider.create(el, {
        start: [startLo, startHi],
        connect: true,
        step: cfg.step,
        range: { min: cfg.min, max: cfg.max },
        tooltips: [true, true],
        format: {
          to: (v) => fmtUSD.format(Math.round(v)),
          from: (s) => Number(String(s).replace(/[^\d.-]/g, "")),
        },
        ariaFormat: { to: (v) => `$${Math.round(v)}` },
      });

      const syncPlaceholders = (_, __, unformatted) => {
        const [lo, hi] = unformatted;
        if (minRef.current && document.activeElement !== minRef.current && !minRef.current.value) {
          minRef.current.placeholder = fmtUSD.format(Math.round(lo));
        }
        if (maxRef.current && document.activeElement !== maxRef.current && !maxRef.current.value) {
          maxRef.current.placeholder = fmtUSD.format(Math.round(hi));
        }
      };

      const fireCommit = (_, __, unformatted) => {
        const [lo, hi] = unformatted;
        commitRef.current?.({
          type: activeType,
          min: Math.round(lo),
          max: Math.round(hi),
          hasMin: lo > cfg.min,
          hasMax: hi < cfg.max,
        });
      };

      el.noUiSlider.on("update", syncPlaceholders);
      el.noUiSlider.on("change", fireCommit);
      syncPlaceholders(null, null, [startLo, startHi]);

      return () => {
        if (el.noUiSlider) {
          el.noUiSlider.off("update", syncPlaceholders);
          el.noUiSlider.off("change", fireCommit);
          el.noUiSlider.destroy();
        }
      };
    }, [activeType, range?.min, range?.max, value, LIMITS, fmtUSD]);

    const parseMoney = (s) => {
      if (!s) return null;
      const n = Number(String(s).replace(/[^\d.-]/g, ""));
      return Number.isFinite(n) ? n : null;
    };

    const setCaretToEnd = (el) => {
      const p = el.value.length;
      try { el.setSelectionRange(p, p); } catch { }
    };

    const ensureDollarOnFocus = (e) => {
      const el = e.target;
      if (!el.value) { el.value = "$"; setCaretToEnd(el); }
      else if (!el.value.startsWith("$")) { el.value = "$" + el.value.replace(/^\$+/, ""); setCaretToEnd(el); }
    };

    const handleMoneyTyping = (e) => {
      const el = e.target;
      let raw = el.value.replace(/[^\d]/g, "");
      raw = raw.replace(/^0+(?=\d)/, "");
      el.value = "$" + raw;
      setCaretToEnd(el);
    };

    const preventDeletingDollar = (e) => {
      const el = e.target;
      const pos = el.selectionStart ?? 0;
      if ((e.key === "Backspace" && pos <= 1) || (e.key === "ArrowLeft" && pos <= 1)) {
        e.preventDefault();
        el.setSelectionRange(1, 1);
      }
    };

    const applyInputsToSlider = React.useCallback(() => {
      const el = sliderRef.current;
      if (!el?.noUiSlider) return;
      const cfg = LIMITS[activeType];

      let lo = parseMoney(minRef.current?.value);
      let hi = parseMoney(maxRef.current?.value);

      if (lo == null) lo = cfg.min;
      if (hi == null) hi = cfg.max;

      lo = clampTo(lo, cfg);
      hi = clampTo(hi, cfg);
      if (lo > hi) [lo, hi] = [hi, lo];

      el.noUiSlider.set([lo, hi]);
    }, [LIMITS, activeType]);

    const handleMoneyBlur = (e) => {
      const el = e.target;
      const cfg = LIMITS[activeType];
      const n = parseMoney(el.value);
      if (n == null) {
        el.value = "";
      } else {
        el.value = fmtUSD.format(Math.round(clampTo(n, cfg)));
      }
      applyInputsToSlider();
    };

    const clearAll = React.useCallback(() => {
      const el = sliderRef.current;
      if (!el?.noUiSlider) return;
      const cfg = LIMITS[activeType];
      if (minRef.current) minRef.current.value = "";
      if (maxRef.current) maxRef.current.value = "";
      el.noUiSlider.set([cfg.min, cfg.max]);
      commitRef.current?.({
        type: activeType,
        min: cfg.min,
        max: cfg.max,
        hasMin: false,
        hasMax: false,
      });
    }, [LIMITS, activeType]);

    return (
      <div className="salary-panel">
        <fieldset className="pay-type">
          <label>
            <input
              type="radio"
              name="payType"
              value="hourly"
              checked={activeType === "hourly"}
              onChange={() => onTypeChange?.("hourly")}
            />{" "}
            Per Hour
          </label>
          <label>
            <input
              type="radio"
              name="payType"
              value="daily"
              checked={activeType === "daily"}
              onChange={() => onTypeChange?.("daily")}
            />{" "}
            Daily
          </label>
          <label>
            <input
              type="radio"
              name="payType"
              value="annual"
              checked={activeType === "annual"}
              onChange={() => onTypeChange?.("annual")}
            />{" "}
            Annual
          </label>
        </fieldset>

        <div className="slider-inputs-container">
          <div className="salary-slider" ref={sliderRef} aria-label="Salary range slider" />
          <div className="inputs">
            <label>
              Min
              <input
                ref={minRef}
                inputMode="numeric"
                placeholder="0"
                onFocus={ensureDollarOnFocus}
                onInput={handleMoneyTyping}
                onKeyDown={preventDeletingDollar}
                onBlur={handleMoneyBlur}
                aria-label="Minimum pay"
              />
            </label>
            <span className="dash">—</span>
            <label>
              Max
              <input
                ref={maxRef}
                inputMode="numeric"
                placeholder="0"
                onFocus={ensureDollarOnFocus}
                onInput={handleMoneyTyping}
                onKeyDown={preventDeletingDollar}
                onBlur={handleMoneyBlur}
                aria-label="Maximum pay"
              />
            </label>
          </div>
        </div>

        <div className="actions">
          <button type="button" className="secondary" onClick={clearAll}>Clear</button>
        </div>
      </div>
    );
  }



  const currentPayValue = React.useMemo(() => {
    switch (frequencyDetails.payType) {
      case "hourly": return frequencyDetails.payRateHourly;
      case "daily": return frequencyDetails.payRateDaily;
      case "annual": return frequencyDetails.payRateAnnual;
      default: return undefined;
    }
  }, [
    frequencyDetails.payType,
    frequencyDetails.payRateHourly,
    frequencyDetails.payRateDaily,
    frequencyDetails.payRateAnnual
  ]);

  const currentRange = React.useMemo(() => {
    return frequencyDetails.payRanges?.[frequencyDetails.payType] ?? { min: null, max: null };
  }, [frequencyDetails.payType, frequencyDetails.payRanges]);

  return (
    <div className="frequency-dates-times-component">
      <div className="frequency-dates-times-scrollable" ref={daySelectionRef}>
        {isFrequencyError && visibleErrors.length > 0 && (
          <div ref={errorMessageRef} className="fdtc-error-message">
            <ul>
              {visibleErrors.map((e, i) => (
                <li key={i}>
                  {e.message}{" "}
                  {getScrollRef(e.field) && (
                    <button
                      type="button"
                      className="fdtc-fix-btn"
                      onClick={() => {
                        const r = getScrollRef(e.field);
                        r?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                        if (e.field === "sessionsPerWeek") {
                          sessionsPerWeekRef.current?.focus();
                        } else if (e.field === "hoursPerSession") {
                          hoursPerSessionRef.current?.focus();
                        }
                      }}
                    >
                      Fix
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>

        )}


        <h4>Job Type</h4>
        <div
          ref={refs.jobType}
          className={`fdtc-radio-container-group ${hasErrorForField("jobType") ? "error-highlight" : ""}`}>
          {JOB_TYPE_OPTIONS.map((opt, i) => (
            <div className="fdtc-radio-container" key={opt}>
              <div className="radio-label-container">
                <input
                  type="radio"
                  name="jobType"
                  id={`jobType-${i}`}
                  value={opt}
                  checked={frequencyDetails.jobType === opt}
                  onChange={() => handleChangeJobType(opt)}
                />
                <label htmlFor={`jobType-${i}`}>{opt}</label>
              </div>
            </div>
          ))}
        </div>

        {showShiftTypePicker && (
          <>
            <h4>Shift Schedule</h4>
            <div className="shifts-wrapper">
              <ul className="support-activity-list">
                {SHIFT_TYPE_OPTIONS.map(({ key, label }) => {
                  const selected = (frequencyDetails.shiftTypes ?? []).includes(key);
                  return (
                    <li
                      key={key}
                      className={`support-activity-item ${selected ? "selected" : ""}`}
                      role="checkbox"
                      aria-checked={selected}
                      tabIndex={0}
                      onClick={() => toggleShiftType(key)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleShiftType(key);
                        }
                      }}
                    >
                      <span className="support-activity-icon" aria-hidden="true">✓</span>
                      <span className="support-activity-text">{label}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </>
        )}

        <h4>Session Type</h4>

        <div
          ref={refs.supportType}
          className={`fdtc-radio-container-group ${hasErrorForField("supportType") ? "error-highlight" : ""
            }`}
        >
          <div className={`fdtc-radio-container ${frequencyDetails.supportType === "Ongoing" ? "selected" : ""}`}>
            <div className="radio-label-container">
              <input
                type="radio"
                id="ongoingSupport"
                value="Ongoing"
                checked={frequencyDetails.supportType === "Ongoing"}
                onChange={() => {
                  handleChangeSupportType("Ongoing");
                }}
                aria-expanded={frequencyDetails.supportType === "Ongoing"}
                aria-controls="ongoing-detail"
              />
              <label htmlFor="ongoingSupport">Recurring Support</label>
            </div>
          </div>

          <div className={`fdtc-radio-container ${frequencyDetails.supportType === "One-Off" ? "selected" : ""}`}>
            <div className="radio-label-container">
              <input
                type="radio"
                id="oneOffSupport"
                value="One-Off"
                checked={frequencyDetails.supportType === "One-Off"}
                onChange={() => {
                  handleChangeSupportType("One-Off");
                }}
                aria-expanded={frequencyDetails.supportType === "One-Off"}
                aria-controls="oneoff-detail"
              />
              <label htmlFor="oneOffSupport">Single Session</label>
            </div>
          </div>
        </div>

        <h4>Time Schedule Preferences</h4>
        <div
          ref={refs.scheduling}
          className={`fdtc-radio-container-group ${hasErrorForField("scheduling") ? "error-highlight" : ""
            }`}
        >

          <div className="fdtc-radio-container">
            <div className="radio-label-container">
              <input
                type="radio"
                id="flexible"
                value="Flexible"
                name="scheduling"
                checked={frequencyDetails.scheduling === "Flexible"}
                onChange={() => {
                  handleChangeScheduling("Flexible");
                }}
                aria-expanded={frequencyDetails.scheduling === "Flexible"}
                aria-controls="flexible-detail"
              />
              <label htmlFor="flexible">I'm open to any schedule</label>
            </div>

            {frequencyDetails.scheduling === "Flexible" && (
              <div id="flexible-detail" className="fdtc-inline-details">
                <textarea
                  id="flexible-notes"
                  ref={flexibleNotesRef}
                  rows={1}
                  maxLength={100}
                  value={frequencyDetails.additionalDetails || ""}
                  onChange={handleChangeAdditionalDetails}
                  placeholder="Add any scheduling notes (optional) e.g., evenings, rotating shifts, weekend preferences, etc."
                  className="fdtc-textarea"
                  aria-describedby="flexible-notes-count"
                />
                <div id="flexible-notes-count" className="fdtc-char-count">
                  {(frequencyDetails.additionalDetails?.length || 0)}/100
                </div>
              </div>
            )}
          </div>

          <div className="fdtc-radio-container">
            <div className="radio-label-container">
              <input
                type="radio"
                id="preferred"
                value="Preferred"
                name="scheduling"
                checked={frequencyDetails.scheduling === "Preferred"}
                onChange={() => {
                  handleChangeScheduling("Preferred");
                }}
              />
              <label htmlFor="preferred">I prefer specific days and times</label>
            </div>
          </div>

          {isPreferred && (
            <div
              ref={refs.preferredDays}
              className={`day-selection-container ${hasErrorForField("preferredDays") ? "error-highlight" : ""}`}
            >
              {DAYS.map((day) => {
                const isOn = !!(frequencyDetails.preferredDays?.[day]);
                const slots = frequencyDetails.timeSlots?.[day] ?? [];

                return (
                  <div key={day} className="fdtc-toggle-container">
                    <div className="toggle-and-label-container">
                      <div
                        className={isOn ? "toggle-switch active" : "toggle-switch"}
                        onClick={() => toggleDay(day)}
                        role="switch"
                        aria-checked={isOn}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleDay(day);
                          }
                        }}
                      >
                        <div className="toggle-circle" />
                      </div>
                      <label className={isOn ? "timeslot-active-label" : ""}>{day}</label>
                    </div>

                    {isOn && (
                      <div className="fdtc-time-slots-container">
                        {slots.map((slot, index) => (
                          <div key={`${day}-${index}`} className="fdtc-time-fields-wrapper">
                            <div className="fdtc-time-fields-error-wrapper">
                              <div className="fdtc-time-fields">
                                <div className="fdtc-time-select">
                                  <label htmlFor={`${day}-from-${index}`}>From</label>
                                  <select
                                    id={`${day}-from-${index}`}
                                    value={slot.from}
                                    onChange={(e) =>
                                      handleChangeTimeSlot(day, index, "from", e.target.value)
                                    }
                                  >
                                    {timeSlotsData.timeSlots.map((timeSlot, timeSlotIndex) => (
                                      <option key={timeSlotIndex} value={timeSlot}>
                                        {timeSlot}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                <div className="fdtc-time-select">
                                  <label htmlFor={`${day}-to-${index}`}>To</label>
                                  <select
                                    id={`${day}-to-${index}`}
                                    value={slot.to}
                                    onChange={(e) =>
                                      handleChangeTimeSlot(day, index, "to", e.target.value)
                                    }
                                  >
                                    {timeSlotsData.timeSlots.map((timeSlot, timeSlotIndex) => (
                                      <option key={timeSlotIndex} value={timeSlot}>
                                        {timeSlot}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                <div className="fdtc-remove-button-container">
                                  {slots.length > 1 && (
                                    <button
                                      type="button"
                                      className="remove-time-slot-btn"
                                      onClick={() => removeTimeSlot(day, index)}
                                    >
                                      Remove
                                    </button>
                                  )}
                                </div>
                              </div>

                              {slot.error && (
                                <div className="fdtc-time-error">{slot.error}</div>
                              )}
                            </div>
                          </div>
                        ))}

                        <button
                          type="button"
                          className="add-time-slot-btn"
                          onClick={() => addTimeSlot(day)}
                          disabled={slots.some((s) => s.error)}
                        >
                          + Add a time slot
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {showSessionFields && (
          <div className="fdtc-radio-container-group">
            {frequencyDetails.supportType === "Ongoing" && (
              <div id="ongoing-detail" className="fdtc-inline-details">
                <div ref={refs.sessionsPerWeek} className={`frequency-hour-session-ongoing ${hasErrorForField("sessionsPerWeek") ? "error-highlight" : ""}`}>
                  <h5>Sessions per Week</h5>
                  <span className="user-dashboard-arrow">➤</span>
                  <div className="weekly-session-input">
                    {frequencyDetails.scheduling === "Preferred" ? (
                      <input
                        type="number"
                        value={sessionsPerWeekAuto}
                        readOnly
                        disabled
                        aria-readonly="true"
                        title="Derived from selected days & time slots"
                      />
                    ) : (
                      <input
                        type="number"
                        ref={sessionsPerWeekRef}
                        min="1"
                        step="1"
                        value={frequencyDetails.sessionsPerWeek ?? ""}
                        onFocus={handleSessionsFocus}
                        onChange={handleChangeSessionsPerWeek}
                        aria-label="Sessions per week"
                      />
                    )}
                  </div>
                </div>

                <div ref={refs.hoursPerSession} className={`frequency-hour-session-onetime ${hasErrorForField("hoursPerSession") ? "error-highlight" : ""}`}>
                  <h5>Hours per Session</h5>
                  <span className="user-dashboard-arrow">➤</span>
                  <div className="weekly-session-input">
                    {frequencyDetails.scheduling === "Preferred" ? (
                      durationsHuman.length ? (
                        <div className="fdtc-time-per-session-list">
                          {durationsHuman.map((d, i) => (
                            <span key={i} className="slot-chip">{d}</span>
                          ))}
                        </div>
                      ) : (
                        <span>-</span>
                      )
                    ) : (
                      <>
                        <input
                          type="number"
                          step="0.5"
                          min="0.5"
                          ref={hoursPerSessionRef}
                          value={frequencyDetails.hoursPerSession ?? ""}
                          onFocus={handleTotalHoursFocus}
                          onChange={handleChangeHoursPerSession}
                          aria-label="Hours per session"
                        />
                        <span className="suffix">
                          {(frequencyDetails.hoursPerSession ?? 0) === 1 ? "hr" : "hrs"}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}


            {frequencyDetails.supportType === "One-Off" && (
              <div id="oneoff-detail" className="fdtc-inline-details">

                {frequencyDetails.scheduling === "Flexible" ? (
                  <div
                    className={`frequency-hour-session-onetime ${hasErrorForField("hoursPerSession") ? "error-highlight" : ""}`}
                  >
                    <h5>Hours per Session</h5>
                    <div className="input-group">
                      <input
                        type="number"
                        step="0.5"
                        min="0.5"
                        ref={hoursPerSessionRef}
                        value={frequencyDetails.hoursPerSession ?? ""}
                        onFocus={handleTotalHoursFocus}
                        onChange={handleChangeHoursPerSession}
                        aria-label="Hours per session"
                      />
                      <span className="suffix">hrs</span>
                    </div>
                  </div>
                ) : (
                  <div
                    className={`frequency-hour-session-onetime ${hasErrorForField("hoursPerSession") ? "error-highlight" : ""}`}
                  >
                    <h5>Hours per Session</h5>
                    <div className="input-group">
                      {durationsHuman.length ? (
                        <div className="fdtc-time-per-session-list">
                          {durationsHuman.map((d, i) => (
                            <span key={i} className="slot-chip">{d}</span>
                          ))}
                        </div>
                      ) : (
                        <span>-</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <h4>Pay Type</h4>
        <div
          ref={refs.pay}
          className={`fdtc-radio-container-group ${hasErrorForField("pay") ? "error-highlight" : ""}`}>
          <SalaryPanel
            type={frequencyDetails.payType}
            value={currentPayValue}
            range={currentRange}
            onTypeChange={(t) => {
              const changes = {
                payType: t,
                currency: "USD",
                payRateHourly: null,
                payRateDaily: null,
                payRateAnnual: null,
              };

              const blank = { min: null, max: null };
              const nextRanges = {
                hourly: { ...(frequencyDetails.payRanges?.hourly || blank) },
                daily: { ...(frequencyDetails.payRanges?.daily || blank) },
                annual: { ...(frequencyDetails.payRanges?.annual || blank) },
              };

              (["hourly", "daily", "annual"]).forEach((k) => {
                if (k !== t) nextRanges[k] = { ...blank };
              });

              if (
                (nextRanges[t].min == null && nextRanges[t].max == null)
              ) {
                const lim = PAY_LIMITS[t];
                nextRanges[t] = { min: lim.min, max: lim.max };
              }

              changes.payRanges = nextRanges;

              notifyUpdate(changes);
              clearErrorAndFields("payRateHourly");
            }}

            onCommit={({ type, min, max, hasMin, hasMax }) => {
              const equal = min === max;

              const currentRanges = frequencyDetails.payRanges || {
                hourly: { min: null, max: null },
                daily: { min: null, max: null },
                annual: { min: null, max: null },
              };
              const nextRanges = { ...currentRanges };

              const nextMin = hasMin ? min : null;
              const nextMax = hasMax ? max : (hasMin ? max : null);

              const PAY = { ...PAY_LIMITS };

              const changes = {
                payType: type,
                currency: "USD",
                payRateHourly: null,
                payRateDaily: null,
                payRateAnnual: null,
                payRanges: { ...nextRanges },
              };

              if (equal) {
                if (type === "hourly") {
                  changes.payRateHourly = clampTo(min, PAY.hourly);
                } else if (type === "daily") {
                  changes.payRateDaily = clampTo(min, PAY.daily);
                } else if (type === "annual") {
                  changes.payRateAnnual = clampTo(min, PAY.annual);
                }
                changes.payRanges[type] = { min: null, max: null };
              } else {
                changes.payRanges[type] = { min: nextMin, max: nextMax };
              }

              (["hourly", "daily", "annual"]).forEach((k) => {
                if (k !== type) changes.payRanges[k] = { min: null, max: null };
              });

              notifyUpdate(changes);
              clearErrorAndFields("payRateHourly");
            }}


          />
        </div>

        <h4>Preferred Support Start Date </h4>
        <div
          ref={refs.supportStartDate}
          className={`fdtc-radio-container-group ${hasErrorForField("supportStartDate") ? "error-highlight" : ""
            }`}
        >
          <div className="fdtc-radio-container">
            <div className="radio-label-container">
              <input
                type="radio"
                id="asap"
                value="As Soon As Possible"
                checked={dateSelectionType === "As Soon As Possible"}
                onChange={() => {
                  handleChangeSupportStartDate("As Soon As Possible");
                }}
              />
              <label htmlFor="asap">Start immediately</label>
            </div>
          </div>
          <div className="fdtc-radio-container">
            <div className="radio-label-container">
              <input
                type="radio"
                id="specificDate"
                value="specificDate"
                checked={dateSelectionType === "specificDate"}
                onChange={() => {
                  handleChangeSupportStartDate("specificDate");
                }}
              />
              <label htmlFor="specificDate">Set a start date</label>
            </div>
          </div>

          {dateSelectionType === "specificDate" && (
            <div className="specifiDate-time-select">
              <select
                id="daySelect"
                name="day"
                value={selectedDay}
                onChange={handleChangeSelectedDay}
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                  <option value={day} key={day}>
                    {day}
                  </option>
                ))}
              </select>
              <select
                id="monthSelect"
                name="month"
                value={selectedMonth}
                onChange={handleChangeSelectedMonth}
              >
                {frequencyMonths.map((month, index) => (
                  <option value={index + 1} key={month}>
                    {month}
                  </option>
                ))}
              </select>
              <select
                id="yearSelect"
                name="year"
                value={selectedYear}
                onChange={handleChangeSelectedYear}
              >
                {Array.from(
                  { length: 5 },
                  (_, i) => new Date().getFullYear() + i
                ).map((year) => (
                  <option value={year} key={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default FrequencyDatesTimesComponent;
